import { useState } from 'react';

const features = [
  {
    icon: 'ri-rfid-line',
    title: 'Embedded RFID Tags',
    description: 'Each tire is fitted with a durable RFID chip embedded directly into the rubber, surviving the full lifecycle of the tire.'
  },
  {
    icon: 'ri-database-2-line',
    title: 'Full Lifecycle Tracking',
    description: 'From manufacture to disposal, every tire movement, fitment, and service event is recorded in a tamper-proof digital ledger.'
  },
  {
    icon: 'ri-bar-chart-box-line',
    title: 'Fleet Intelligence',
    description: 'Real-time dashboards give fleet operators instant visibility into tire health, mileage, pressure history, and compliance status.'
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Counterfeit Prevention',
    description: 'Unique cryptographic identifiers make it impossible to substitute or misrepresent tires, protecting brands and end users alike.'
  },
  {
    icon: 'ri-recycle-line',
    title: 'Sustainability Reporting',
    description: 'Automated ESG reporting tracks retreading rates, carbon footprint, and end-of-life disposal compliance across entire fleets.'
  },
  {
    icon: 'ri-cloud-line',
    title: 'Cloud-Native Platform',
    description: 'A SaaS platform accessible from any device, integrating with existing fleet management, ERP, and telematics systems via open APIs.'
  }
];

const stats = [
  { value: '100M+', label: 'Tires Trackable' },
  { value: '99.9%', label: 'Read Accuracy' },
  { value: '10yr+', label: 'Tag Lifespan' },
  { value: '360°', label: 'Lifecycle Visibility' }
];

export default function TireRFID() {
  const [activeFeature, setActiveFeature] = useState(0);

  return (
    <section className="relative bg-[#F7F4EF]">
      {/* Top divider */}
      <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#C9A96E]/30 to-transparent"></div>

      {/* Hero Banner */}
      <div className="relative py-24 bg-[#0A0F1E] overflow-hidden">
        {/* Background texture */}
        <div className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 40px, #C9A96E 40px, #C9A96E 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, #C9A96E 40px, #C9A96E 41px)'
          }}
        ></div>

        <div className="relative max-w-[1200px] mx-auto px-12">
          {/* Top row: Text left, Video right */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left — Text */}
            <div className="space-y-10">
              <div className="flex items-center gap-5">
                <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
                <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                  Portfolio Company
                </p>
              </div>

              <div className="space-y-4">
                <h2 className="text-5xl font-light text-white leading-[1.15] tracking-[-0.02em]">
                  Tire Intelligence<br />
                  <span className="font-semibold">Powered by RFID</span>
                </h2>
                <div className="flex items-center gap-3 pt-2">
                  <span className="text-[22px] font-bold tracking-widest text-[#C9A96E]">D//VY</span>
                  <span className="text-white/30 text-sm">|</span>
                  <span className="text-white/50 text-[13px] tracking-wide">diivy.com</span>
                </div>
              </div>

              <p className="text-[16px] leading-[1.9] text-white/60 font-light max-w-[480px]">
                DIIVY is a pioneering tire lifecycle management platform that embeds RFID technology directly into tires — enabling full traceability, fleet intelligence, and sustainability compliance from manufacture to disposal.
              </p>

              <p className="text-[15px] leading-[1.8] text-white/50 font-light max-w-[480px]">
                Operating across automotive, logistics, and heavy industry sectors, DIIVY transforms tires from a commodity into a connected, data-rich asset — reducing costs, improving safety, and delivering measurable ESG outcomes.
              </p>

              <a
                href="https://www.diivy.com"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-3 px-8 py-3.5 border border-[#C9A96E]/40 text-[13px] font-semibold text-[#C9A96E] rounded-sm hover:bg-[#C9A96E] hover:text-[#0A0F1E] hover:border-[#C9A96E] transition-all duration-300 whitespace-nowrap tracking-wide"
              >
                Visit DIIVY
                <i className="ri-external-link-line text-sm"></i>
              </a>
            </div>

            {/* Right — Video */}
            <div className="rounded-sm overflow-hidden border border-white/10">
              <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
                <iframe
                  src="https://www.youtube.com/embed/3NezmOWCRL8?si=PXxEfmd8TfYVzpeJ"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                  title="DIIVY Tire RFID Platform"
                ></iframe>
              </div>
            </div>
          </div>

          {/* Bottom row — Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-16 border-t border-white/5">
            {stats.map((stat, i) => (
              <div
                key={i}
                className="p-8 border border-white/5 rounded-sm bg-white/[0.03] hover:border-[#C9A96E]/30 hover:bg-white/[0.05] transition-all duration-500 text-center"
              >
                <p className="text-4xl font-bold text-[#C9A96E] tracking-[-0.02em]">{stat.value}</p>
                <p className="mt-2 text-[12px] font-medium text-white/40 tracking-[0.12em] uppercase">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="py-28 bg-[#F7F4EF]">
        <div className="max-w-[1200px] mx-auto px-12">
          {/* Section Header */}
          <div className="mb-20 text-center space-y-6">
            <div className="flex items-center justify-center gap-5">
              <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
              <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                Platform Capabilities
              </p>
              <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
            </div>
            <h2 className="text-4xl font-light text-[#0A0F1E] leading-[1.2] tracking-[-0.02em] max-w-[600px] mx-auto">
              End-to-End Tire<br />
              <span className="font-semibold">Lifecycle Management</span>
            </h2>
            <p className="text-[15px] leading-[1.8] text-gray-500 font-light max-w-[560px] mx-auto">
              DIIVY's platform covers every stage of a tire's life — giving manufacturers, fleet operators, and regulators a single source of truth.
            </p>
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`group p-8 rounded-sm border cursor-pointer transition-all duration-400 ${
                  activeFeature === index
                    ? 'border-[#C9A96E]/50 bg-white shadow-sm'
                    : 'border-[#0A0F1E]/8 bg-white/60 hover:border-[#C9A96E]/30 hover:bg-white hover:shadow-sm'
                }`}
                onClick={() => setActiveFeature(index)}
              >
                <div className="space-y-4">
                  <div className="w-11 h-11 flex items-center justify-center rounded-sm bg-[#C9A96E]/10 group-hover:bg-[#C9A96E]/20 transition-colors duration-300">
                    <i className={`${feature.icon} text-xl text-[#C9A96E]`}></i>
                  </div>
                  <h3 className="text-[16px] font-semibold text-[#0A0F1E] tracking-[-0.01em]">
                    {feature.title}
                  </h3>
                  <p className="text-[14px] leading-[1.75] text-gray-500 font-light">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom divider */}
      <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#C9A96E]/30 to-transparent"></div>
    </section>
  );
}
